import { appKey } from '@app/shared/brand'
/**
 * The answers a donor gave, kept across a Checkout return or reload.
 *
 * Embedded Checkout stays in the card, but a payment method may still return
 * through the page address and a reload still loses React state. The answers
 * keep the acknowledgement specific and let an unfinished payment resume.
 *
 * `sessionStorage` rather than `localStorage`: this is one act in one tab, and
 * an answer that outlived the tab would put a stranger's gift in front of
 * whoever opens the page next on a shared machine. Every access is guarded —
 * private browsing and blocked site data both throw rather than returning
 * nothing, and a donation form must work either way.
 */

/** The plain gift: the one branch whose answers this page carries through. */
export const GIFT = 'donation'

export type DonateAnswers = {
  /** The branch the answers were given under — `GIFT` from this page. Read
   *  as a string, since storage may hold any value, and anything else is
   *  resumed nowhere (`resumable`). */
  branch: string
  /** The menu path that was open, parent first. */
  open: string[]
  chargeItemId: string | null
  /** What the gift was for, as the donor read it back. Null for a plain gift. */
  forLabel: string | null
  description: string
  /** The question the donor's words answered, so the thank-you can name it. */
  noteLabel?: string | null
  amountCents: number
  /** How often, where the item left it to the giver; a stored gift is once. */
  often?: 'once'
  coverFee: boolean
  /** The card may stay on file for what this household pays next. */
  saveCard: boolean
  donorName: string
  donorEmail: string
  /** The checkout these answers were opened as, once one has opened. */
  checkoutId?: string
}

/**
 * The answers to pick a gift up again from, or none.
 *
 * A drive is asked on its own page, so an answer naming one resumes nothing
 * here: its menu path, words and amount answered questions this page does not
 * ask of a drive. It still names the gift in a thank-you, which reads the
 * stored answers rather than these.
 */
export function resumable(answers: DonateAnswers | null): DonateAnswers | null {
  return answers?.branch === GIFT ? answers : null
}

/** One key per org, so two shuls open in two tabs cannot read each other's. */
const keyFor = (orgId: string) => appKey('donate', orgId)

export function rememberAnswers(orgId: string, answers: DonateAnswers): void {
  try {
    sessionStorage.setItem(keyFor(orgId), JSON.stringify(answers))
  } catch {
    /* A donor whose browser refuses storage still gives; only the retry after
       an abandoned payment is lost, and that is not worth a refusal. */
  }
}

export function recallAnswers(orgId: string): DonateAnswers | null {
  try {
    const raw = sessionStorage.getItem(keyFor(orgId))
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    return isAnswers(parsed) ? parsed : null
  } catch {
    return null
  }
}

export function forgetAnswers(orgId: string): void {
  try {
    sessionStorage.removeItem(keyFor(orgId))
  } catch {
    /* Nothing to clear where nothing could be written. */
  }
}

/**
 * What came out of storage is text somebody's browser held, so it is checked
 * rather than trusted: a shape that has moved since the answers were written is
 * a form filled in with `undefined`, which reads as a bug on the one page that
 * must not have any.
 */
function isAnswers(value: unknown): value is DonateAnswers {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    typeof v.branch === 'string' &&
    Array.isArray(v.open) &&
    v.open.every((o) => typeof o === 'string') &&
    (v.chargeItemId === null || typeof v.chargeItemId === 'string') &&
    (v.forLabel === null || typeof v.forLabel === 'string') &&
    typeof v.description === 'string' &&
    (v.noteLabel === undefined ||
      v.noteLabel === null ||
      typeof v.noteLabel === 'string') &&
    typeof v.amountCents === 'number' &&
    (v.often === undefined || v.often === 'once') &&
    typeof v.coverFee === 'boolean' &&
    /* Answers stored before the donor was asked carry no such flag, and the
       gift they resume keeps the card as it always did. */
    (v.saveCard === undefined || typeof v.saveCard === 'boolean') &&
    typeof v.donorName === 'string' &&
    typeof v.donorEmail === 'string' &&
    (v.checkoutId === undefined || typeof v.checkoutId === 'string')
  )
}
